<template>
  <div class="card">
    <div class="card-body p-4 pt-3">
      <div class="p-2 pt-0">
        <div class="d-flex justify-content-between align-items-center">
          <div class="card-title">Cards</div>
          <button class="back-btn" @click="$emit('back')" :disabled="disableBack">&#8592; Back</button>
        </div>
      <div class="d-flex flex-column align-items-center justify-content-center">
        <div class="activate-title">Activate your new Taxi1 driver prepaid card</div>
        <img src="@/assets/images/driver/small-card.png" class="activate-card-img my-4" alt="card" />
        <div class="activate-desc mb-3">Please enter the EAID number found on the back of your new card. (case sensitive)</div>
          <input 
            type="text" 
            class="form-control activate-input mb-4" 
            placeholder="EAID number" 
            v-model="eaidNumber"
            :class="{ 'is-invalid': errors.eaid }"
            :disabled="isActivating"
          />
          <label class="pds-row" for="pds-agree-mobile">
            <span class="pds-check-label">
              <input
                id="pds-agree-mobile"
                v-model="pdsAccepted"
                type="checkbox"
                class="pds-check-input"
              />
              <span class="pds-check-box" aria-hidden="true" />
            </span>
            <span class="pds-text">
              <span class="pds-line">By activating my Taxi1 card, I confirm</span>
              <span class="pds-line">I have read and agree to the <a href="https://taxi1.com.au/wp-content/uploads/2026/04/PDS.pdf" class="pds-link" target="_blank" rel="noopener noreferrer" @click.stop>PDS</a></span>
            </span>
          </label>
          <button 
            class="btn btn-green activate-btn" 
            @click="handleActivate"
            :disabled="isActivating || !eaidNumber.trim() || !pdsAccepted"
          >
            {{ isActivating ? 'Activating...' : 'Activate' }}
          </button>
        </div>
      </div>
    </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import { useCardStore } from '@/store/driver/card'
import Swal from 'sweetalert2'

const props = defineProps({
  disableBack: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['back', 'success'])

const cardStore = useCardStore()
const eaidNumber = ref('')
const pdsAccepted = ref(false)
const isActivating = ref(false)
const errors = ref({
  eaid: ''
})

const validateForm = () => {
  errors.value.eaid = ''
  
  if (!eaidNumber.value.trim()) {
    errors.value.eaid = 'EAID number is required'
    return false
  }

  if (!pdsAccepted.value) {
    return false
  }

  return true
}

const handleActivate = async () => {
  if (!validateForm()) {
    return
  }
  
  isActivating.value = true
  
  try {
    await cardStore.activateCard(eaidNumber.value.trim())
    
    // 成功時導向卡片詳情頁面
    emit('success')
    
  } catch (error) {
    console.error('Card activation error:', error)
    
    // 處理錯誤訊息
    let errorMessage = 'Activation failed, please try again later'
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }
    
    // 使用 SweetAlert 顯示錯誤訊息
    // Swal.fire({
    //   icon: 'error',
    //   title: errorMessage,
    //   confirmButtonText: 'OK'
    // })
    
  } finally {
    isActivating.value = false
  }
}
</script>

<style scoped>
.card {
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  z-index: 1;
  height: 100%;
  width: 100%; /* 設置寬度為 100% */
  overflow-y: auto;
  overflow-x: hidden; /* 防止左右滑動 */
}
.card-body {
  min-height: 100vh;
  overflow-y: auto;
  padding-bottom: 32px;
  box-sizing: border-box;
  background: #fff;
}
.card-title {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 30px;
  color: #051C2C;
  margin-left: 20px;
}
.back-btn {
  top: 18px;
  right: 24px;
  background: #00c962;
  border: 1px solid #dbe2ea;
  border-radius: 20px;
  padding: 6px 18px;
  font-size: 1rem;
  color: #12223b;
  cursor: pointer;
  z-index: 2;
  transition: background 0.2s;
}
.back-btn:hover:not(:disabled) {
  background: #00c962;
  color: #fff;
}
.back-btn:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
  opacity: 0.6;
}
.activate-title {
    margin-top: 50px;
    font-family: 'Euclid Square';
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    color: #051C2C;
}
.activate-card-img {
  max-width: 375px;
  width: 100%;
  border-radius: 12px;
}
.activate-desc {
    padding: 0 20px;
    font-family: 'Mulish';
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 23px;
    color: #051C2C;
}
.activate-input {
  max-width: 320px;
  width: 100%;
  border-radius: 8px;
  border: 1px solid #dbe2ea;
  padding: 10px 16px;
  font-size: 1rem;
  text-align: center;
}
.activate-btn {
  width: 320px;
  background: #00c962;
  color: #051C2C;
  border-radius: 24px;
  padding: 10px 32px;
  font-size: 1rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  min-width: 180px;
}
.activate-btn:hover {
  background: #00c962;
  color: #fff;
}

.activate-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.error-message {
  color: #ff4040;
  font-size: 0.875rem;
  margin-top: 4px;
  margin-bottom: 8px;
  text-align: center;
}

.is-invalid {
  border-color: #ff4040 !important;
}

.pds-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  max-width: 320px;
  padding: 0 20px;
  box-sizing: border-box;
  margin-bottom: 1.25rem;
  cursor: pointer;
}

.pds-check-label {
  position: relative;
  flex-shrink: 0;
  margin: 2px 0 0 0;
}

.pds-check-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
}

.pds-check-box {
  display: block;
  position: relative;
  width: 22px;
  height: 22px;
  border: 2px solid #c5ced6;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.pds-check-input:focus-visible + .pds-check-box {
  outline: 2px solid #00c962;
  outline-offset: 2px;
}

.pds-check-input:checked + .pds-check-box {
  background: #00c962;
  border-color: #00c962;
}

.pds-check-input:checked + .pds-check-box::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  width: 5px;
  height: 10px;
  margin-left: -3px;
  margin-top: -7px;
  border: solid #051c2c;
  border-width: 0 2.5px 2.5px 0;
  transform: rotate(45deg);
}

.pds-text {
  font-family: 'Mulish', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 23px;
  color: #051c2c;
  flex: 1;
  min-width: 0;
  text-align: left;
}

.pds-line {
  display: block;
}

.pds-link {
  color: #051c2c;
  text-decoration: underline;
  font-weight: 600;
}

/* 手機小螢幕優化 */
@media (max-width: 400px) {
  .card-title {
    font-size: 18px;
    margin-left: 8px;
  }
  .activate-title {
    font-size: 18px;
    margin-top: 24px;
  }
  .activate-card-img {
    max-width: 220px;
    border-radius: 8px;
  }
  .activate-desc {
    font-size: 15px;
    padding: 0 8px;
  }
  .activate-input,
  .activate-btn {
    width: 90vw;
    min-width: unset;
    max-width: 98vw;
    font-size: 0.95rem;
    padding: 8px 8px;
  }
  .pds-row {
    max-width: 98vw;
    width: 90vw;
    padding: 0 8px;
  }
  .pds-text {
    font-size: 15px;
    line-height: 21px;
  }
  .back-btn {
    top: 8px;
    right: 8px;
    padding: 4px 10px;
    font-size: 0.95rem;
  }
}
</style> 